import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useOrg } from '../app/OrgProvider';
import { AppLink } from '../components/Nav';
import { CheckIcon, DownloadIcon, XIcon } from '../components/icons';
import { Badge, Banner, Button, ErrorState, ErrorText, Input, Loading, PageHeader, Spinner, Textarea, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { ApiError, errorMessage } from '../lib/api';
import { durationLabel, timeAgo } from '../lib/format';
import { paywallPath } from '../lib/plans';
import { fetchMe } from '../queries/account';
import {
  dismissImport,
  fetchImports,
  isImportActive,
  loomLinksIn,
  submitLoomImports,
  type LoomImport,
} from '../queries/imports';
import { qk } from '../queries/keys';

/**
 * Bringing a Loom library across.
 *
 * Paste links, one or many, and each becomes a Slate recording with its
 * title, its transcript and its description carried over. The page is the
 * progress: the API works through the batch a couple at a time and the
 * list below the form is polled until everything has landed or said why
 * it could not. There is nothing to download and re-upload by hand, and
 * nothing to install.
 */

const POLL_MS = 2000;

const STATUS_LABEL: Record<LoomImport['status'], string> = {
  queued: 'Waiting',
  fetching: 'Fetching',
  uploading: 'Uploading',
  done: 'Imported',
  failed: 'Failed',
};

function prefilled(): string {
  const url = new URLSearchParams(window.location.search).get('url');
  return url && /loom\.com\//i.test(url) ? url : '';
}

function ImportForm({ onSubmitted }: { onSubmitted: () => void }) {
  const client = useQueryClient();
  const { org } = useOrg();
  const [text, setText] = useState(prefilled);
  const [password, setPassword] = useState('');
  const [rejected, setRejected] = useState<Array<{ url: string; error: string }>>([]);
  const field = useRef<HTMLTextAreaElement>(null);
  const links = useMemo(() => loomLinksIn(text), [text]);

  useEffect(() => {
    field.current?.focus();
  }, []);

  const submit = useMutation({
    mutationFn: (urls: string[]) => submitLoomImports(urls, password.trim() || undefined),
    onSuccess: (result, urls) => {
      track('Loom Import Submitted', { count: urls.length, workspace: org ? 'team' : 'personal' });
      setRejected(result.rejected);
      setText('');
      setPassword('');
      void client.invalidateQueries({ queryKey: qk.imports() });
      onSubmitted();
    },
  });

  const quota = submit.error instanceof ApiError && submit.error.isQuota;

  return (
    <form
      className="rounded-md border border-hairline bg-slate-900 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (links.length === 0 || submit.isPending) return;
        setRejected([]);
        submit.mutate(links.slice(0, 50));
      }}
    >
      <Textarea
        ref={field}
        label="Loom links"
        rows={5}
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={'https://www.loom.com/share/…\nhttps://www.loom.com/share/…'}
        spellCheck={false}
        className="font-mono text-caption"
      />
      <div className="mt-3 max-w-xs">
        <Input
          label="Loom password"
          type="password"
          autoComplete="off"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="Only if the Loom asks for one"
          hint="Applied to every link in this batch. Not kept once the import finishes."
        />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button type="submit" variant="primary" busy={submit.isPending} disabled={links.length === 0}>
          {links.length <= 1 ? 'Import recording' : `Import ${links.length} recordings`}
        </Button>
        <p className="text-caption text-chalk-faint">
          One link per line, or paste a whole column from a spreadsheet. Up to fifty at a time.
        </p>
      </div>
      {submit.isError && !quota ? <div className="mt-3"><ErrorText>{errorMessage(submit.error)}</ErrorText></div> : null}
      {quota ? (
        <div className="mt-3">
          <Banner
            tone="info"
            title="Importing comes with a paid seat"
            action={
              <AppLink to={paywallPath('imports')} onClick={() => track('Paywall Opened', { feature: 'imports' })}>
                <Button variant="primary" size="sm">
                  Upgrade
                </Button>
              </AppLink>
            }
          >
            {errorMessage(submit.error)}
          </Banner>
        </div>
      ) : null}
      {rejected.length > 0 ? (
        <ul className="mt-3 space-y-1">
          {rejected.map((item) => (
            <li key={item.url}>
              <ErrorText>
                <span className="font-mono">{item.url}</span>: {item.error}
              </ErrorText>
            </li>
          ))}
        </ul>
      ) : null}
    </form>
  );
}

function ProgressBar({ value }: { value: number }) {
  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-slate-800" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}>
      <div className="h-full rounded-full bg-signal transition-[width] duration-500" style={{ width: `${Math.max(value, 2)}%` }} />
    </div>
  );
}

function ImportRow({ item, onDismiss }: { item: LoomImport; onDismiss: () => void }) {
  const active = isImportActive(item.status);
  const tone = item.status === 'done' ? 'go' : item.status === 'failed' ? 'danger' : 'info';
  return (
    <li className="flex gap-4 rounded-md border border-hairline bg-slate-900 p-3">
      <div className="hidden w-28 shrink-0 overflow-hidden rounded bg-slate-800 sm:block" style={{ aspectRatio: '16 / 9' }}>
        {item.thumbnailUrl ? (
          <img src={item.thumbnailUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-chalk-faint">
            {active ? <Spinner size={14} /> : <DownloadIcon size={16} />}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-body text-chalk" title={item.title ?? item.sourceUrl}>
              {item.title ?? item.sourceUrl}
            </p>
            <p className="mt-0.5 truncate text-caption text-chalk-faint">
              {item.durationSec ? `${durationLabel(item.durationSec)} · ` : ''}
              {item.status === 'done' ? `Imported ${timeAgo(item.finishedAt)}` : `Added ${timeAgo(item.createdAt)}`}
            </p>
          </div>
          <Badge tone={tone} pulse={active}>
            {STATUS_LABEL[item.status]}
          </Badge>
        </div>
        {active ? (
          <div className="mt-3">
            <ProgressBar value={item.progress} />
          </div>
        ) : null}
        {item.status === 'failed' && item.error ? (
          <div className="mt-2">
            <ErrorText>{item.error}</ErrorText>
          </div>
        ) : null}
        {!active ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {item.video ? (
              <AppLink to="/v/$shareId" params={{ shareId: item.video.shareId }}>
                <Button size="sm" className="gap-1.5">
                  <CheckIcon size={12} />
                  Open recording
                </Button>
              </AppLink>
            ) : null}
            <Button size="sm" variant="ghost" className="gap-1.5" onClick={onDismiss}>
              <XIcon size={12} />
              Dismiss
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}

function ImportList({ imports }: { imports: LoomImport[] }) {
  const client = useQueryClient();
  const dismiss = useMutation({
    mutationFn: (id: string) => dismissImport(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.imports() }),
  });

  // The page was watching these; say how each one ended, once.
  const seen = useRef(new Map<string, LoomImport['status']>());
  useEffect(() => {
    for (const item of imports) {
      const before = seen.current.get(item.id);
      if (before && isImportActive(before) && !isImportActive(item.status)) {
        track('Loom Import Finished', { status: item.status });
      }
      seen.current.set(item.id, item.status);
    }
  }, [imports]);

  const running = imports.filter((i) => isImportActive(i.status)).length;
  const done = imports.filter((i) => i.status === 'done').length;

  if (imports.length === 0) return null;
  return (
    <section className="mt-8">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-h4 text-chalk">Your imports</h2>
        <p className="text-caption text-chalk-faint">
          {running > 0 ? `${running} in progress · ` : ''}
          {done} imported
        </p>
      </div>
      <ul className="space-y-2">
        {imports.map((item) => (
          <ImportRow key={item.id} item={item} onDismiss={() => dismiss.mutate(item.id)} />
        ))}
      </ul>
    </section>
  );
}

function Pitch() {
  return (
    <div className="rounded-md border border-hairline bg-slate-900 px-6 py-10 text-center">
      <DownloadIcon size={22} className="mx-auto mb-3 text-chalk-faint" />
      <p className="text-body text-chalk">Bring your whole Loom library across</p>
      <p className="mx-auto mt-1 max-w-md text-caption text-chalk-dim">
        A paid seat imports every Loom you paste as a Slate recording: the video, its title, its
        description and its transcript. Nothing to download and upload again by hand.
      </p>
      <AppLink to={paywallPath('imports')} onClick={() => track('Paywall Opened', { feature: 'imports' })} className="mt-4 inline-block">
        <Button variant="primary">Upgrade</Button>
      </AppLink>
    </div>
  );
}

export function ImportLoomPage() {
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, staleTime: 60_000 });
  const imports = useQuery({
    queryKey: qk.imports(),
    queryFn: fetchImports,
    enabled: Boolean(me.data?.paidSeat),
    refetchInterval: (query) =>
      query.state.data?.imports.some((i) => isImportActive(i.status)) ? POLL_MS : false,
  });

  useEffect(() => {
    track('Viewed Import Page');
  }, []);

  return (
    <div className={cx('mx-auto w-full max-w-3xl')}>
      <PageHeader
        title="Import from Loom"
        subtitle="Paste Loom share links and they come across as Slate recordings, transcripts and all."
      />

      {me.isPending ? (
        <Loading />
      ) : me.data?.paidSeat ? (
        <>
          <ImportForm onSubmitted={() => void imports.refetch()} />
          <p className="mt-3 text-caption text-chalk-faint">
            Only Looms shared with a link can be brought in. For a private one, open it on Loom, set
            Share to anyone with the link, then paste it here. A password protected Loom comes in
            with its password.
          </p>
          {imports.isError ? (
            <div className="mt-6">
              <ErrorState message={errorMessage(imports.error)} onRetry={() => void imports.refetch()} />
            </div>
          ) : null}
          <ImportList imports={imports.data?.imports ?? []} />
        </>
      ) : (
        <Pitch />
      )}
    </div>
  );
}
